import React, { useRef, useMemo, useState, useEffect } from 'react';

// 间距配置
export interface IFlexItemLayoutSpacingOptions {
  // 水平方向的间距
  horizontalSpacing?: number;
  // 纵向的间距
  verticalSpacing?: number;
}

interface IFlexItemLayoutInfo {
  marginRight: number;
  marginBottom: number;
}

// 水平间距
export const FLEX_ITEM_HORIZONTAL_SPACING = 10;

// 纵向间距
export const FLEX_ITEM_VERTICAL_SPACING = 10;

/**
 * 获取marginRight值
 * 1、第一个、最后一个marginRight为0
 * 2、每一行最后一个marginRight为0，否则为FLEX_ITEM_HORIZONTAL_SPACING
 * @param i 元素索引
 * @param length 元素数量
 * @param indexs 每一行最后一个元素的索引
 * @returns marginRight值
 */
const getMarginRight = (i: number, length: number, indexs: number[], horizontalSpacing: number): number => {
  return horizontalSpacing;
  // if (i === length - 1) return 0;
  // return indexs.includes(i) ? 0 : horizontalSpacing;
};

/**
 * 获取getMarginBottom值
 * 1、只有一行时，marginBottom为0
 * 2、多行时，最后一行的marginBottom为0，否则为FLEX_ITEM_VERTICAL_SPACING
 * @param i 元素索引
 * @param length 元素数量
 * @param indexs 每一行最后一个元素的索引
 * @returns getMarginBottom值
 */
const getMarginBottom = (i: number, length: number, indexs: number[], verticalSpacing: number): number => {
  return verticalSpacing;
  // const indexsLength = indexs.length;
  // // 只有一行时，marginBottom为0
  // if (indexsLength === 1 && length - 1 === indexs[indexsLength - 1]) return 0;
  // // 多行时，最后一行的marginBottom为0，否则为FLEX_ITEM_VERTICAL_SPACING
  // return i > indexs[indexsLength - 2] ? 0 : verticalSpacing;
};

export default function useFlexSpacingLayoutInfo(
  root: React.RefObject<HTMLElement | null>,
  deps?: React.DependencyList,
  options?: IFlexItemLayoutSpacingOptions,
) {
  const { horizontalSpacing = FLEX_ITEM_HORIZONTAL_SPACING, verticalSpacing = FLEX_ITEM_VERTICAL_SPACING } =
    options || {};

  // 元素的数量
  const size = useRef(0);
  // 保存上一个元素的top（遍历中）
  const top = useRef<number | null>(null);

  // 每一行最后一个元素的索引
  const [indexs, setIndexs] = useState<number[]>([]);

  // 每一个索引的元素的布局信息（marginBottom，marginRight）
  const flexItemsLayoutInfos = useMemo<IFlexItemLayoutInfo[]>(() => {
    if (!Array.isArray(indexs) || !indexs.length) return [];
    const result: IFlexItemLayoutInfo[] = [];

    for (let i = 0; i < size.current; i++) {
      const marginRight = getMarginRight(i, size.current, indexs, horizontalSpacing);
      const marginBottom = getMarginBottom(i, size.current, indexs, verticalSpacing);
      result.push({ marginRight, marginBottom });
    }

    return result;
  }, [indexs]);

  const updateFlexItemsLayoutInfos = () => {
    if (!root.current) return;
    const indexsTmp: number[] = [];
    const nodes = root.current.children;
    size.current = nodes.length;
    // 每次重新计算时，都需要把上一个元素的top指向null
    top.current = null;

    /**
     * 遍历元素集合
     * 若是检测到某个元素的top值不为null，并且和上一个元素不相同，则认为该元素换行了，需要把上一个元素的索引添加到indexsTmp中，作为上一行的最后一个元素的索引
     */
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      const { top: nodeTop } = node?.getBoundingClientRect() || {};

      // 不是第一个元素，且当前元素top和前一个元素top不相等，说明换行了
      if (top.current !== null && top.current !== nodeTop) {
        indexsTmp.push(i - 1);
      }
      // 把上一个元素的top指向nodeTop，用于跟下一个元素的top比较，判定是否换行
      top.current = nodeTop;
    }

    // 最后一行的索引就是nodes的长度减一
    indexsTmp.push(nodes.length - 1);
    setIndexs(indexsTmp);
  };

  useEffect(() => {
    updateFlexItemsLayoutInfos();

    window.addEventListener('resize', updateFlexItemsLayoutInfos, false);

    return () => {
      window.removeEventListener('resize', updateFlexItemsLayoutInfos, false);
    };
  }, [deps]);

  return flexItemsLayoutInfos;
}
